import { API_URL, WEB_URL } from "../config.js";

// Lightweight wrapper around Better Auth's HTTP endpoints. We avoid hard-coding
// `better-auth/react` here so the rest of the app can swap clients later
// without touching pages.

async function readAuthError(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { message?: string; error?: string };
    return body.message ?? body.error ?? `${fallback} (${res.status})`;
  } catch {
    return `${fallback} (${res.status})`;
  }
}

export const authClient = {
  async session() {
    const res = await fetch(`${API_URL}/api/auth/get-session`, { credentials: "include" });
    if (!res.ok) return null;
    return res.json() as Promise<{ user?: { id: string; email: string; name?: string } } | null>;
  },

  async signInEmail(email: string, password: string) {
    const res = await fetch(`${API_URL}/api/auth/sign-in/email`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) throw new Error(await readAuthError(res, "Sign in failed"));
    return res.json();
  },

  async signUpEmail(email: string, password: string, name?: string) {
    const res = await fetch(`${API_URL}/api/auth/sign-up/email`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, name: name ?? email.split("@")[0] }),
    });
    if (!res.ok) throw new Error(await readAuthError(res, "Sign up failed"));
    return res.json();
  },

  // Better Auth has no GET /api/auth/google shortcut. Social sign-in is a POST
  // to /api/auth/sign-in/social that returns the provider authorize { url }; the
  // browser then navigates there. (The old GET <a href> 404'd — that was the
  // "No webpage was found for ...api/auth/google" crash users hit.)
  async signInGoogle(callbackPath?: string) {
    const path = callbackPath && callbackPath.startsWith("/") ? callbackPath : "/app";
    const res = await fetch(`${API_URL}/api/auth/sign-in/social`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      // Absolute callback so Better Auth resolves it against the web origin
      // (a trustedOrigin), not the API baseURL.
      body: JSON.stringify({ provider: "google", callbackURL: `${WEB_URL}${path}` }),
    });
    if (!res.ok) throw new Error(await readAuthError(res, "Google sign-in failed"));
    const data = (await res.json()) as { url?: string };
    if (!data.url) throw new Error("Google sign-in is not available right now");
    window.location.href = data.url;
  },

  async forgotPassword(email: string, next?: string | null) {
    // If a `next` destination is provided we append it to redirectTo so the
    // /reset-password page (and the /signin page after successful reset) can
    // carry the user back to where they started.
    const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : null;
    const redirectTo = safeNext
      ? `${WEB_URL}/reset-password?next=${encodeURIComponent(safeNext)}`
      : `${WEB_URL}/reset-password`;
    // Better Auth renamed this endpoint from /forget-password → /request-password-reset
    // somewhere in the 1.x line. Older docs / our previous client still pointed
    // at /forget-password and were silently 404-ing — the UI just showed the
    // "if an account exists..." confirmation regardless.
    const res = await fetch(`${API_URL}/api/auth/request-password-reset`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, redirectTo }),
    });
    if (!res.ok) throw new Error(await readAuthError(res, "Request failed"));
  },

  async resetPassword(token: string, newPassword: string) {
    const res = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, newPassword }),
    });
    if (!res.ok) throw new Error(await readAuthError(res, "Password reset failed"));
  },

  async signOut() {
    // Better Auth rejects POST without Content-Type: application/json (415).
    // Previously we sent no body and no header — the request silently failed,
    // the session cookie was never cleared, and users who clicked "Sign out"
    // were redirected to /, still signed in.
    const res = await fetch(`${API_URL}/api/auth/sign-out`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
    if (!res.ok) throw new Error(await readAuthError(res, "Sign out failed"));
  },
};
